import ReportHdrIcon from './ReportHdrIcon';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Button
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { FEATURE_MULTI_STORE } from '../config/features';
import { storeApi } from '../services/api';

const MobileHeader: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [storeName, setStoreName] = useState<string>('주방을 담다');
  const [myStores, setMyStores] = useState<{ storeId: number; storeName?: string }[]>([]);
  const [storeMenuAnchor, setStoreMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useAuth();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
    handleClose();
  };

  // 사업장명 가져오기
  useEffect(() => {
    const fetchStoreName = async () => {
      try {
        // currentUser에서 사업장명 가져오기
        if (currentUser?.storeName) {
          setStoreName(currentUser.storeName);
          return;
        }
        
        // currentUser에 없으면 API에서 가져오기
        const storeId = currentUser?.storeId || Number(localStorage.getItem('storeId') || 0);
        if (storeId) {
          const resp = await storeApi.getStoreInfo(storeId);
          const data: any = resp?.data || {};
          const fetchedName = data.name || data.Name || data.storeName || data.StoreName;
          if (typeof fetchedName === 'string' && fetchedName.trim()) {
            setStoreName(fetchedName);
          } else {
            // 사업장명을 가져올 수 없으면 기본값 유지
            setStoreName('주방을 담다');
          }
        } else {
          // storeId가 없으면 기본값 유지
          setStoreName('주방을 담다');
        }
      } catch (e) {
        // 오류 발생 시 기본값 유지
        console.error('사업장명 가져오기 실패:', e);
        setStoreName('주방을 담다');
      }
    };
    
    fetchStoreName();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const load = async () => {
      try {
        const res = await storeApi.getMyStores();
        const list = res?.data?.stores ?? [];
        setMyStores(Array.isArray(list) ? list : []);
      } catch {
        setMyStores([]);
      }
    };
    load();
    const onInvalidated = () => load();
    window.addEventListener('judam:stores-invalidated', onInvalidated);
    return () => window.removeEventListener('judam:stores-invalidated', onInvalidated);
  }, [currentUser]);

  const handleStoreSwitch = (storeId: number, name?: string) => {
    setStoreMenuAnchor(null);
    localStorage.setItem('storeId', String(storeId));
    if (name) localStorage.setItem('storeName', name);
    setCurrentUser((prev) => (prev ? { ...prev, storeId, storeName: name ?? prev.storeName } : null));
    window.location.reload();
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        display: { xs: 'block', md: 'none' },
        background: 'rgba(10, 48, 44, 0.92) !important',
        backgroundColor: 'rgba(10, 48, 44, 0.92) !important',
        backgroundImage: 'none !important',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        color: '#ffffff',
        zIndex: 1201,
        top: 0,
        left: 0,
        right: 0,
        height: 'calc(56px + env(safe-area-inset-top, 0px))',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        boxShadow: 'none',
        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        '@media (prefers-reduced-transparency: reduce)': {
          background: '#0A302C !important',
          backgroundColor: '#0A302C !important',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none'
        },
        '& .MuiToolbar-root': {
          minHeight: '56px !important',
          height: '56px !important',
          padding: '0 16px',
          background: 'transparent !important',
          backgroundColor: 'transparent !important'
        },
        borderRadius: 0
      }}
      elevation={0}
    >
      <Toolbar 
        sx={{ 
          minHeight: '56px !important',
          height: '56px !important',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minWidth: 0, flex: 1, mr: 1 }}>
          <Box
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', mr: 0.5, flexShrink: 0 }}
            role="button"
            aria-label="대시보드로 이동"
            tabIndex={0}
            onClick={() => {
              sessionStorage.setItem('skipMultiStoreRedirectAt', String(Date.now()));
              navigate('/dashboard', { state: { skipMultiStoreRedirect: true } });
            }}
            onKeyDown={(e) => { 
              if (e.key === 'Enter' || e.key === ' ') { 
                e.preventDefault();
                sessionStorage.setItem('skipMultiStoreRedirectAt', String(Date.now()));
                navigate('/dashboard', { state: { skipMultiStoreRedirect: true } });
              } 
            }}
          >
            <img
              src="/logo120.png"
              alt="주담 클라우드 로고"
              style={{ height: '28px', width: '28px', marginRight: '12px', objectFit: 'contain' }}
            />
          </Box>
          {myStores.length >= 2 ? (
            <>
              <Button
                color="inherit"
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => { e.stopPropagation(); setStoreMenuAnchor(e.currentTarget); }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.9375rem',
                  color: '#fff',
                  minWidth: 0,
                  maxWidth: '100%',
                  px: 0.5,
                  justifyContent: 'flex-start',
                  '& .MuiButton-endIcon': { flexShrink: 0, ml: 0.25 },
                  '& .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: 18 }
                }}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  {storeName}
                </Box>
              </Button>
              <Menu
                anchorEl={storeMenuAnchor}
                open={Boolean(storeMenuAnchor)}
                onClose={() => setStoreMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {myStores
                  .filter((s) => s.storeId !== (currentUser?.storeId ?? parseInt(localStorage.getItem('storeId') || '0', 10)))
                  .map((s) => (
                    <MenuItem key={s.storeId} onClick={() => handleStoreSwitch(s.storeId, s.storeName)}>
                      {s.storeName || `매장 #${s.storeId}`}
                    </MenuItem>
                  ))}
              </Menu>
            </>
          ) : (
            <Typography
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                fontSize: '0.9375rem',
                color: '#ffffff',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'pointer'
              }}
              onClick={() => {
                sessionStorage.setItem('skipMultiStoreRedirectAt', String(Date.now()));
                navigate('/dashboard', { state: { skipMultiStoreRedirect: true } });
              }}
            >
              {storeName}
            </Typography>
          )}
        </Box>

        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={handleMenu}
          color="inherit"
          sx={{
            p: 0.5,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <Avatar sx={{ 
            width: 28,  // 모바일 모드에서 프로필 아이콘 크기 축소
            height: 28, // 모바일 모드에서 프로필 아이콘 크기 축소
            backgroundColor: 'transparent'
          }}>
            <ReportHdrIcon file="icons8-customer-100" w={28} />
          </Avatar>
        </IconButton>

        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          sx={{
            '& .MuiPaper-root': {
              minWidth: 180,
              mt: 1,
              borderRadius: 1,
              boxShadow: '0 12px 32px rgba(25,35,45,0.14)'
            }
          }}
        >
          {FEATURE_MULTI_STORE && myStores.length >= 2 && (
            <MenuItem 
              onClick={() => { navigate('/multi-store'); handleClose(); }}
              sx={{ 
                py: 1.5, 
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <ReportHdrIcon file="icons8-online-store-100" w={18} />
              다점포 관리
            </MenuItem>
          )}
          <MenuItem 
            onClick={handleProfile}
            sx={{ 
              py: 1.5, 
              px: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <ReportHdrIcon file="icons8-test-account-100" w={18} />
            프로필
          </MenuItem>
          <MenuItem 
            onClick={handleSettings}
            sx={{ 
              py: 1.5, 
              px: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <ReportHdrIcon file="icons8-settings-100" w={18} />
            설정
          </MenuItem>
          <MenuItem 
            onClick={handleLogout}
            sx={{ 
              py: 1.5, 
              px: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              color: 'error.main'
            }}
          >
            <ReportHdrIcon file="icons8-logout-100" w={18} />
            로그아웃
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default MobileHeader; 